import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Moment } from 'moment';
import { Img } from 'react-image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { Button, ControlledTextField2, Spinner } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { AppointmentMultiple } from 'model/NewMultipleBooking';
import Invoice from '@spike/invoice-model';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import SendReceiptDialog from '../../UI/SendReceiptDialog';
import { PaymentsStatus, sendThunk } from '@spike/payments-action';
import { useApiClientWrapper } from 'hooks';
import { Link } from 'react-router-dom';
import useNonInitialEffect from '@versiondos/hooks';
import { FieldError } from '@spike/model';
import { isEmpty } from 'lodash';
import Client from '@spike/client-model';
import { isEmailValid } from '@spike/validations';

interface AppointmentCreatedProps {
    multipleAppointments: Array<AppointmentMultiple>;
    withDeposit?: boolean;
    date?: Moment;
    petId?: number;
    depositPaidAmount?: number;
    onGoToBooking?: () => void;
    onBooked?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '85%',
            alignItems: 'center'
        },
        containerWithDeposit: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            alignItems: 'center'
        },
        header: {
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            height: '55%',
            width: 346,
            paddingTop: 80
        },
        content: {
            display: 'flex',
            justifyContent: 'center'
        },
        icon: {
            color: '#5E8677',
            width: 82,
            height: 82,
            paddingBottom: 11,
            [theme.breakpoints.up('md')]: {
                width: 97,
                height: 97,
                paddingBottom: 13
            }
        },
        textContainer: {
            display: 'flex',
            height: 210
        },
        title: {
            fontWeight: 600,
            fontSize: 26,
            lineHeight: '32px',
            paddingBottom: 11,
            [theme.breakpoints.up('md')]: {
                fontSize: 32,
                lineHeight: '35px',
                paddingBottom: 13
            }
        },
        text: {
            fontSize: 16,
            lineHeight: '22px'
        },
        bold: {
            fontWeight: 600
        },
        normal: {
            fontWeight: 400
        },
        imageContainer: {
            display: 'flex',
            justifyContent: 'center',
            height: '65%',
            width: '100%',
            textAlign: 'center'
        },
        image: {
            color: '#5E8677',
            height: '97%'
        },
        depositAmount: {
            color: '#5E8677',
            fontWeight: 500,
            fontSize: 28,
            lineHeight: '34px',
            marginBottom: 10
        },
        depositText: {
            fontWeight: 500,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 30
        },
        button: {
            width: 265,
            height: 55,
            marginBottom: 20
        },
        sendReceipt: {
            display: 'flex',
            cursor: 'pointer'
        },
        paperIcon: {
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '24px',
            paddingRight: 5,
            paddingTop: 5
        },
        sendReceiptText: {
            fontWeight: 500,
            fontSize: 18,
            lineHeight: '27px',
            textDecoration: 'underline'
        },
        titleDialog: {
            [theme.breakpoints.down('sm')]: {
                fontSize: '26px !important',
                lineHeight: '26px !important',
                textAlign: 'center',
                marginBottom: 40
            },
            textAlign: 'start',
            marginLeft: 10,
            fontWeight: 600,
            fontSize: 18,
            lineHeight: '22px',
            marginTop: 5
        },
        emailContainer: {
            marginLeft: 32,
            marginRight: 27
        }
    })
);

export const AppointmentCreated: FunctionComponent<AppointmentCreatedProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const depositInvoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.depositInvoice);

    const client = useSelector<RootState, Client | undefined>(state => state.clients.client);

    const [sendShowReceipt, setSendShowReceipt] = useState(false);

    const [email, setEmail] = useState((client?.temporalEmail ? '' : depositInvoice?.customer?.email) || '');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const multiplePetsOrServices = () => {
        if (
            props.multipleAppointments.length > 1 ||
            (props.multipleAppointments.length === 1 && props.multipleAppointments[0].variableData.length > 1)
        ) {
            return 'Appointments were';
        }
        return 'Appointment was';
    };

    const validateEmail = (): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        if (!email || isEmpty(email.trim())) {
            errors.push({
                fieldName: 'email',
                errorMessage: 'Email is required'
            });
        }

        if (email && email?.trim()?.length > 0 && !isEmailValid(email || '')) {
            errors.push({
                fieldName: 'email',
                errorMessage: 'Email incorrect format'
            });
        }

        return errors;
    };

    useNonInitialEffect(() => {
        if (PaymentsStatus.Sending === paymentsStatus) {
            setLoading(true);
        } else if (PaymentsStatus.SendSuccess === paymentsStatus) {
            setSendShowReceipt(false);
            props.onBooked && props.onBooked();
            setLoading(false);
        } else if (PaymentsStatus.Error === paymentsStatus) {
            setLoading(false);
        }
    }, [paymentsStatus]);

    useEffect(() => {
        const errors = validateEmail();
        setErrors(errors);
    }, [email]);

    const headerComponent = (children: JSX.Element) => (
        <Box className={classes.header}>
            <FontAwesomeIcon
                icon={faCheckCircle}
                className={classes.icon}
            />
            <Typography
                id="booking_label_appointment_booked"
                className={classes.title}
            >
                Appointment Booked
            </Typography>
            <Box className={classes.textContainer}>{children}</Box>
        </Box>
    );

    const appointmentCreated = (
        <Box className={classes.container}>
            {headerComponent(
                <Box className={classes.textContainer}>
                    <Typography className={classes.text}>
                        {multiplePetsOrServices()} booked for{' '}
                        {props.multipleAppointments[0]?.variableData[0]?.date?.format('dddd, MMM Do h:mm a') ??
                            props.date?.format('dddd, MMM Do h:mm a')}
                    </Typography>
                </Box>
            )}
            <Box className={classes.imageContainer}>
                <Img
                    src="/images/bookings/AppointmentCreated.svg"
                    loader={<Spinner />}
                    className={classes.image}
                />
            </Box>
        </Box>
    );

    const appointmentCreatedWithDeposit = (
        <Box className={classes.containerWithDeposit}>
            {headerComponent(
                <Box className={classes.textContainer}>
                    <Typography className={classes.text}>
                        Appointment was booked for{' '}
                        {props.multipleAppointments[0]?.variableData[0]?.date?.format('dddd, MMM Do h:mm a') ??
                            props.date?.format('dddd, MMM Do h:mm a')}
                    </Typography>
                </Box>
            )}
            <>
                <Typography className={classes.depositAmount}>${props.depositPaidAmount?.toFixed(2)}</Typography>
                <Typography className={classes.depositText}>Total Paid</Typography>
            </>
            <>
                <Link to={'/payments#TRANSACTIONS'}>
                    <Button
                        label="View Transaction"
                        className={classes.button}
                        color="black"
                    />
                </Link>
                <Button
                    label="Go to Booking"
                    color="black"
                    className={classes.button}
                    onClick={props.onGoToBooking}
                />
                <Box
                    className={classes.sendReceipt}
                    onClick={() => setSendShowReceipt(true)}
                >
                    <FontAwesomeIcon
                        icon={faPaperPlane}
                        className={classes.paperIcon}
                    />
                    <Typography className={classes.sendReceiptText}>Send Receipt</Typography>
                </Box>
            </>
        </Box>
    );

    return (
        <>
            {props.withDeposit ? appointmentCreatedWithDeposit : appointmentCreated}
            {sendShowReceipt && (
                <SendReceiptDialog
                    open={true}
                    isSuccess={true}
                    title={<Typography className={classes.titleDialog}>Send Receipt</Typography>}
                    question={
                        <Box className={classes.emailContainer}>
                            <ControlledTextField2
                                label="Email"
                                placeholder="Email Address"
                                name="email"
                                value={email}
                                onChange={setEmail}
                                autoComplete={'off'}
                                errors={errors}
                            />
                        </Box>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Send"
                    processing={loading}
                    disabled={errors.length > 0}
                    onCancel={() => setSendShowReceipt(false)}
                    onConfirm={() => {
                        if (errors.length === 0) {
                            dispatch(sendThunk(apiClientWrapper, depositInvoice!.id!, true, false, false, email));
                        }
                    }}
                />
            )}
        </>
    );
};

export default AppointmentCreated;
